import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import {
  datePickerUniDriverFactory,
  DatePickerUniDriver,
} from 'wix-style-react-tpa/dist/src/DatePicker/DatePicker.uni.driver';
import { inputDriverFactory as coreInputDriverFactory } from '../internal/WixUiCoreComponents/components/CoreInput/CoreInput.uni.driver';
import {
  textFieldDriverFactory as textFieldUniDriverFactory,
  TextFieldDriver,
} from '../TextField/TextField.driver';
import { dataHook } from '../../testkitUtils/utils';
import { DATA_HOOKS } from './constants';
import {
  IconButtonDriver,
  iconButtonDriverFactory,
} from '../IconButton/IconButton.driver';
import { focusable, FocusableDriver } from '../../test/unidriver-mixins';
import { uniDriverImplementation } from '../../test/unidriver-config';

type A11yAttributes = {
  role: string;
  ariaAutocomplete: string;
  ariaControls: string;
  ariaExpanded: string;
  ariaHaspopup: string;
  ariaReadonly: string;
};

const getA11yAttributes = async (
  driver: UniDriver,
): Promise<A11yAttributes> => {
  const [
    role,
    ariaAutocomplete,
    ariaControls,
    ariaExpanded,
    ariaHaspopup,
    ariaReadonly,
  ] = await Promise.all([
    driver.attr('role'),
    driver.attr('aria-autocomplete'),
    driver.attr('aria-controls'),
    driver.attr('aria-expanded'),
    driver.attr('aria-haspopup'),
    driver.attr('aria-readonly'),
  ]);
  return {
    role,
    ariaAutocomplete,
    ariaControls,
    ariaExpanded,
    ariaHaspopup,
    ariaReadonly,
  };
};

const isComboboxAttributesValid = (
  a11yAttributes: A11yAttributes,
  isOpen: boolean,
  popupId?: string,
) => {
  const { role, ariaAutocomplete, ariaControls, ariaExpanded, ariaHaspopup } =
    a11yAttributes;
  const isRoleValid = role === 'combobox';
  const isAriaAutoCompleteValid = ariaAutocomplete === 'none';
  const isAriaExpandedValid = ariaExpanded === (isOpen ? 'true' : 'false');
  const isAriaControlsValid =
    popupId !== undefined && popupId !== null
      ? popupId && ariaControls === popupId
      : !!ariaControls?.length;
  const isAriaHaspopupValid = ariaHaspopup === 'dialog';

  const isValid = [
    isAriaAutoCompleteValid,
    isAriaControlsValid,
    isAriaExpandedValid,
    isAriaHaspopupValid,
    isRoleValid,
  ].every(Boolean);

  if (!isValid) {
    throw new Error(
      'Combobox attributes are not valid\n' +
        [
          !isRoleValid && `Role is ${role}, but is should be combobox`,
          !isAriaAutoCompleteValid &&
            `AriaAutoComplete is ${ariaAutocomplete}, but is should be none`,
          !isAriaControlsValid &&
            `AriaControls is ${ariaControls}, but is should be ${
              popupId ? popupId : 'string'
            }`,
          !isAriaExpandedValid &&
            `AriaExpanded is ${ariaExpanded}, but is should be ${isOpen}`,
          !isAriaHaspopupValid &&
            `AriaHaspopup is ${ariaHaspopup}, but is should be dialog`,
        ]
          .filter(Boolean)
          .join('\n') +
        `\n${popupId}`,
    );
  }

  return true;
};

export interface DatePickerInputDriver extends BaseUniDriver {
  /** @private */
  _base: UniDriver;
  calendar: DatePickerUniDriver['calendarDriver'];
  textField: TextFieldDriver & {
    input: TextFieldDriver['input'] &
      FocusableDriver & {
        /**
         * Returns true if input has combobox role and related aria attributes.
         * @param isOpen - whether the popup is expected to be open
         */
        isCombobox(isOpen?: boolean): Promise<boolean>;
        /**
         * Returns true if input has no role and no aria attributes.
         */
        isWithoutRole(): Promise<boolean>;
      };
    icons: () => Promise<(IconButtonDriver & FocusableDriver)[]>;
  };
  clickOutside: DatePickerUniDriver['driver']['clickOutside'];
  /**
   * open the calender window by clicking on the input
   * @return {Promise<void>}
   */
  openCalendarWindow(): Promise<void>;
  /**
   * Returns true if calendar is open .
   * @deprecated use calendar.exists instead
   * @returns {Promise<boolean>}
   */
  isCalendarOpen(): Promise<boolean>;
  /**
   * Returns input's label text.
   * @returns {Promise<string>}
   */
  getLabelText(): Promise<string>;
  /**
   * Returns true if label exists.
   * @returns {Promise<boolean>}
   */
  isLabelExists(): Promise<boolean>;
}

export const datePickerInputDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): DatePickerInputDriver => {
  const datePickerDriver = datePickerUniDriverFactory(base, body);
  const textFieldUniDriver = textFieldUniDriverFactory(base);

  const coreInputRoot = base.$$(dataHook(DATA_HOOKS.DATE_INPUT)).at(0);
  const coreInputDriver = coreInputDriverFactory(coreInputRoot);

  const getIcons: () => Promise<(IconButtonDriver & FocusableDriver)[]> = () =>
    textFieldUniDriver.root.$$('button').map(async (button) => {
      const driver = iconButtonDriverFactory(button);
      return { ...driver, ...focusable(driver._base) };
    });

  return {
    _base: base,
    ...baseUniDriverFactory(base),

    calendar: datePickerDriver.calendarDriver,
    textField: {
      ...textFieldUniDriver,
      input: {
        ...coreInputDriver.input,
        ...focusable(coreInputDriver.input),
        isCombobox: async (isOpen: boolean) => {
          const a11yAttributes = await getA11yAttributes(coreInputDriver.input);

          const popupId = isOpen
            ? await uniDriverImplementation
                .current(await datePickerDriver.calendarDriver.element())
                .attr('id')
            : undefined;

          return isComboboxAttributesValid(a11yAttributes, isOpen, popupId);
        },
        isWithoutRole: async () => {
          const { ariaReadonly, ...rest } = await getA11yAttributes(
            coreInputDriver.input,
          );

          return (
            ariaReadonly === 'true' &&
            Object.values(rest).every((value) => value === null)
          );
        },
      },
      icons: getIcons,
    },

    clickOutside: datePickerDriver.driver.clickOutside,
    openCalendarWindow: coreInputDriver.input.click,
    isCalendarOpen: datePickerDriver.calendarDriver.exists,
    isLabelExists: textFieldUniDriver.label.exists,
    getLabelText: textFieldUniDriver.label.text,
  };
};
